import { ChevronUp, Copy, Ellipsis, FileClock, FolderKanban, LogOut, Plus, Settings, Share2, ShieldCheck, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { deleteAccount, deleteProject, duplicateProject, fetchProjects, recordJourneyEvent } from '../lib/api';
import { useAuth } from '../lib/auth';
import { trackJourney } from '../lib/journey';
import { isAppRedirect } from '../lib/navigation';
import type { ProjectSummary } from '../types';

function isProjectActive(location: string, projectId: string) {
  return location === `/projects/${projectId}` || location.startsWith(`/projects/${projectId}/`);
}

function projectTimestamp(createdAt: string) {
  return new Date(createdAt).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function ProjectLink({ project, active, menuOpen, onNavigate, onToggleMenu, onRequestDelete, onDuplicate }: {
  project: ProjectSummary;
  active: boolean;
  menuOpen: boolean;
  onNavigate?: () => void;
  onToggleMenu: () => void;
  onRequestDelete: () => void;
  onDuplicate: () => void;
}) {
  return (
    <div className={`project-nav-row ${active ? 'active' : ''}`}>
      <Link href={`/projects/${project.id}`} className="project-nav-link" aria-current={active ? 'page' : undefined} onClick={onNavigate}>
        <span className="project-nav-icon"><FolderKanban size={16} /></span>
        <span><strong>{project.title}</strong><small>{project.sessionCount ? `${project.sessionCount} past practice${project.sessionCount === 1 ? '' : 's'}` : projectTimestamp(project.updatedAt)}</small></span>
      </Link>
      <div className="project-actions">
        <button type="button" className="project-menu-trigger" aria-label={`Project actions for ${project.title}`} aria-haspopup="menu" aria-expanded={menuOpen} onClick={onToggleMenu}>
          <Ellipsis size={19} />
        </button>
        {menuOpen && <div className="project-action-menu" role="menu">
          {project.accessRole === 'owner' && <Link href={`/projects/${project.id}/settings`} role="menuitem" onClick={onNavigate}><Share2 size={16} /> Share project</Link>}
          {project.accessRole !== 'owner' && <button type="button" role="menuitem" onClick={onDuplicate}><Copy size={16} /> Duplicate project</button>}
          {project.accessRole === 'owner' && <button type="button" role="menuitem" className="danger" onClick={onRequestDelete}><Trash2 size={16} /> Delete project</button>}
          {project.accessRole !== 'owner' && <span className="project-member-label">{project.accessRole === 'editor' ? 'Editor access' : 'Viewer access'}</span>}
        </div>}
      </div>
    </div>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const { user, signOut, getToken, configured } = useAuth();
  const [location, navigate] = useLocation();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projectsError, setProjectsError] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const [projectMenuId, setProjectMenuId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProjectSummary | null>(null);
  const [deletingProject, setDeletingProject] = useState(false);
  const [deleteProjectError, setDeleteProjectError] = useState('');
  const openedEventId = useRef(crypto.randomUUID());

  useEffect(() => {
    void trackJourney(getToken, 'app_opened', { clientEventId: openedEventId.current, properties: { path: location } });
  }, [getToken, location]);

  useEffect(() => {
    const load = () => void getToken().then(fetchProjects).then(({ projects: loaded }) => { setProjects(loaded); setProjectsError(''); })
      .catch(() => setProjectsError('Could not load projects.'));
    load();
    window.addEventListener('maxed:project-saved', load);
    return () => window.removeEventListener('maxed:project-saved', load);
  }, [getToken, location]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setSettingsOpen(false);
      setMobileNavOpen(false);
      setAccountSettingsOpen(false);
      setProjectMenuId(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, []);

  useEffect(() => {
    if (isAppRedirect(window.history.state)) return;
    setMobileNavOpen(false);
    setSettingsOpen(false);
    setProjectMenuId(null);
  }, [location]);

  useEffect(() => {
    if (!projectMenuId) return;
    const closeProjectMenu = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest('.project-actions')) setProjectMenuId(null);
    };
    document.addEventListener('mousedown', closeProjectMenu);
    return () => document.removeEventListener('mousedown', closeProjectMenu);
  }, [projectMenuId]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [mobileNavOpen]);

  function closeMobileNav() {
    setMobileNavOpen(false);
    setSettingsOpen(false);
  }

  async function handleDeleteAccount() {
    if (!window.confirm('Delete your Maxed account and all meeting practice data? This cannot be undone.')) return;
    try {
      const token = await getToken();
      await deleteAccount(token);
      await signOut();
    } catch {
      window.alert('Account deletion could not be queued. Please try again.');
    }
  }

  async function handleSignOut() {
    const token = await getToken();
    await signOut();
    try {
      await recordJourneyEvent(token, 'signout_completed');
    } catch {
      // Signing out must not be blocked by analytics delivery.
    }
  }

  function requestProjectDelete(project: ProjectSummary) {
    setProjectMenuId(null);
    setMobileNavOpen(false);
    setDeleteProjectError('');
    setDeleteTarget(project);
  }

  async function handleDuplicateProject(project: ProjectSummary) {
    setProjectMenuId(null);
    setMobileNavOpen(false);
    try {
      const token = await getToken();
      const copy = await duplicateProject(token, project.id);
      window.dispatchEvent(new Event('maxed:project-saved'));
      navigate(`/projects/${copy.project.id}`);
    } catch (cause) {
      window.alert(cause instanceof Error ? cause.message : 'Project could not be duplicated.');
    }
  }

  async function handleDeleteProject() {
    if (!deleteTarget || deletingProject) return;
    setDeletingProject(true);
    setDeleteProjectError('');
    try {
      const token = await getToken();
      await deleteProject(token, deleteTarget.id);
      const remaining = projects.filter((project) => project.id !== deleteTarget.id);
      setProjects(remaining);
      const deletedActiveProject = isProjectActive(location, deleteTarget.id);
      setDeleteTarget(null);
      if (deletedActiveProject) navigate(remaining.length ? `/projects/${remaining[0].id}` : '/');
    } catch (cause) {
      setDeleteProjectError(cause instanceof Error ? cause.message : 'Project could not be deleted.');
    } finally {
      setDeletingProject(false);
    }
  }

  function settingsMenu(className: string) {
    if (!settingsOpen) return null;
    return (
      <div className={`settings-menu ${className}`} role="menu">
        {user?.isAdmin && <Link href="/admin" role="menuitem" onClick={closeMobileNav}><ShieldCheck size={16} /> Admin</Link>}
        <button role="menuitem" onClick={() => { closeMobileNav(); setAccountSettingsOpen(true); }}><Settings size={16} /> Settings</button>
        <a href="/privacy-policy" role="menuitem"><FileClock size={16} /> Privacy policy</a>
        {configured && <button role="menuitem" onClick={() => { closeMobileNav(); void handleSignOut(); }}><LogOut size={16} /> Sign out</button>}
      </div>
    );
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand"><img className="brand-mark" src="/brand/maxed-logo.webp" alt="" /><span>Maxed</span></div>
        <Link href="/" className={`new-practice-link ${location === '/' ? 'active' : ''}`}><Plus size={18} /><span>New project</span></Link>
        <div className="session-nav-heading"><span>Projects</span></div>
        <nav className="session-nav" aria-label="Projects">
          {projects.map((project) => <ProjectLink key={project.id} project={project} active={isProjectActive(location, project.id)} menuOpen={projectMenuId === project.id} onToggleMenu={() => setProjectMenuId((open) => open === project.id ? null : project.id)} onRequestDelete={() => requestProjectDelete(project)} onDuplicate={() => void handleDuplicateProject(project)} />)}
          {!projects.length && projectsError && <p>{projectsError}</p>}
        </nav>
        <div className="sidebar-foot settings-wrap">
          {settingsMenu('desktop-settings-menu')}
          <button className="user-row user-settings-trigger" aria-haspopup="menu" aria-expanded={settingsOpen} onClick={() => setSettingsOpen((open) => !open)}>
            <span className="user-avatar">{user?.name.charAt(0).toUpperCase()}</span>
            <span className="user-copy"><strong>{user?.name}</strong><small>{user?.email}</small></span>
            <ChevronUp className={settingsOpen ? 'open' : ''} size={17} />
          </button>
        </div>
      </aside>
      <div className="content-shell">
        <header className="mobile-header">
          <button className="mobile-menu-button" type="button" aria-label="Open navigation" aria-controls="mobile-navigation" aria-expanded={mobileNavOpen} onClick={() => setMobileNavOpen(true)}>
            <span className="mobile-menu-glyph" aria-hidden="true"><i /><i /></span>
          </button>
          <div className="brand"><img className="brand-mark" src="/brand/maxed-logo.webp" alt="" /><span>Maxed</span></div>
        </header>
        <main className="page">{children}</main>
      </div>
      {mobileNavOpen && <>
        <button className="mobile-drawer-backdrop" type="button" aria-label="Close navigation" onClick={closeMobileNav} />
        <aside id="mobile-navigation" className="mobile-drawer" role="dialog" aria-modal="true" aria-label="Navigation">
          <div className="mobile-drawer-header">
            <div className="brand"><img className="brand-mark" src="/brand/maxed-logo.webp" alt="" /><span>Maxed</span></div>
            <button type="button" aria-label="Close navigation" onClick={closeMobileNav}><X size={20} /></button>
          </div>
          <Link href="/" className={`new-practice-link ${location === '/' ? 'active' : ''}`} onClick={closeMobileNav}><Plus size={18} /><span>New project</span></Link>
          <div className="session-nav-heading"><span>Projects</span></div>
          <nav className="session-nav" aria-label="Mobile projects">
            {projects.map((project) => <ProjectLink key={project.id} project={project} active={isProjectActive(location, project.id)} menuOpen={projectMenuId === project.id} onNavigate={closeMobileNav} onToggleMenu={() => setProjectMenuId((open) => open === project.id ? null : project.id)} onRequestDelete={() => requestProjectDelete(project)} onDuplicate={() => void handleDuplicateProject(project)} />)}
            {!projects.length && projectsError && <p>{projectsError}</p>}
          </nav>
          <div className="mobile-drawer-foot settings-wrap">
            {settingsMenu('mobile-drawer-settings-menu')}
            <button className="user-row user-settings-trigger mobile-user-trigger" aria-haspopup="menu" aria-expanded={settingsOpen} onClick={() => setSettingsOpen((open) => !open)}>
              <span className="user-avatar">{user?.name.charAt(0).toUpperCase()}</span>
              <span className="user-copy"><strong>{user?.name}</strong><small>{user?.email}</small></span>
              <ChevronUp className={settingsOpen ? 'open' : ''} size={17} />
            </button>
          </div>
        </aside>
      </>}
      {deleteTarget && <div className="project-delete-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !deletingProject) setDeleteTarget(null); }}>
        <section className="project-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="project-delete-title">
          <h2 id="project-delete-title">Delete “{deleteTarget.title}”?</h2>
          <p>All imported data and past practice session transcripts in this project will be permanently deleted. This cannot be undone.</p>
          {deleteProjectError && <p className="error-banner">{deleteProjectError}</p>}
          <div className="project-delete-actions">
            <button type="button" className="button secondary" disabled={deletingProject} onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button type="button" className="button danger" disabled={deletingProject} onClick={() => void handleDeleteProject()}>{deletingProject ? 'Deleting...' : 'Delete project'}</button>
          </div>
        </section>
      </div>}
      {accountSettingsOpen && <div className="account-settings-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setAccountSettingsOpen(false); }}>
        <section className="account-settings-dialog" role="dialog" aria-modal="true" aria-labelledby="account-settings-title">
          <button className="account-settings-close" type="button" aria-label="Close settings" onClick={() => setAccountSettingsOpen(false)}><X size={19} /></button>
          <h2 id="account-settings-title">Settings</h2>
          <div className="account-settings-user">
            <span className="user-avatar">{user?.name.charAt(0).toUpperCase()}</span>
            <span><strong>{user?.name}</strong><small>{user?.email}</small></span>
          </div>
          {configured && <section className="account-settings-danger">
            <div><strong>Delete account</strong><p>Permanently delete your account and meeting practice data.</p></div>
            <button type="button" onClick={() => void handleDeleteAccount()}><Trash2 size={16} /> Delete account</button>
          </section>}
        </section>
      </div>}
    </div>
  );
}
